/**
 * useJobsCoordinator - Orchestrates job data fetching strategies
 *
 * Extracted from useJobs.ts for coordinated query management
 * Determines which query strategy to use (default, search, risk)
 * and returns unified interface
 */

import { useEffect, useMemo, useState } from 'react';

import { useJobStats } from '@/hooks/useJobs';
import { JobFilters } from '@/sections/jobs/types';
import { Job } from '@/types';
import { useMergeStatus } from '@/utils/api';

import { useJobsFromRisk } from './useJobsFromRisk';
import { useJobsQuery } from './useJobsQuery';
import { useJobsSearch } from './useJobsSearch';

/**
 * Coordinator hook that orchestrates job queries
 *
 * Determines query strategy based on filters:
 * - Search: Uses 3 parallel search queries (name, DNS, source)
 * - Risk: Fetches jobs related to specific risk
 * - Default: Standard job listing with optional status filter
 *
 * Provides unified interface regardless of strategy used.
 *
 * @param filter - Job filter configuration
 * @returns Unified job query result
 */
export function useJobsCoordinator(filter: JobFilters) {
  const [isFilteredDataFetching, setIsFilteredDataFetching] = useState(false);

  // Determine which query strategy to use
  const isSearched = Boolean(filter.search);
  const isRiskKey = Boolean(filter.riskKey);

  // Fetch job statistics (status counts)
  const { jobStats, status: jobStatsStatus } = useJobStats();

  // Strategy 1: Default query (no search, no risk)
  const defaultQuery = useJobsQuery(filter, !isSearched && !isRiskKey);

  // Strategy 2: Search query (3 parallel paths)
  const searchQuery = useJobsSearch(filter, isSearched);

  // Strategy 3: Risk-based query
  const riskQuery = useJobsFromRisk(filter, isRiskKey);

  // Select active query strategy
  const activeQuery = isSearched
    ? searchQuery
    : isRiskKey
      ? riskQuery
      : defaultQuery;

  // Merge status from active query
  const apiStatus = useMergeStatus(
    ...(Array.isArray(activeQuery.status)
      ? activeQuery.status
      : [activeQuery.status])
  );

  // Override status if we're fetching filtered data
  const status = isFilteredDataFetching ? 'pending' : apiStatus;

  // Extract jobs data from query response
  // Note: Different strategies return different data structures:
  // - defaultQuery.data: GenericResource with .jobs property
  // - searchQuery.data: Job[] array directly
  // - riskQuery.data: GenericResource with .jobs property
  const jobs = useMemo(() => {
    if (!activeQuery.data) return [];

    // If data is already an array (from search strategy), return it directly
    if (Array.isArray(activeQuery.data)) {
      return activeQuery.data;
    }

    // Otherwise extract .jobs property (from default/risk strategy)
    return (activeQuery.data as any)?.jobs || [];
  }, [activeQuery.data]);

  // Auto-fetch next page if we have less than 50 results and more pages available
  // This ensures we show enough data without empty states
  useEffect(() => {
    if (!activeQuery.isFetching) {
      if (activeQuery.hasNextPage && jobs.length < 50) {
        setIsFilteredDataFetching(true);
        activeQuery.fetchNextPage();
      } else {
        setIsFilteredDataFetching(false);
      }
    }
  }, [
    jobs?.length ?? 0,
    filter.search,
    activeQuery.isFetching,
    activeQuery.hasNextPage,
  ]);

  return {
    data: jobs,
    refetch: activeQuery.refetch,
    status,
    fetchNextPage: activeQuery.fetchNextPage,
    error: activeQuery.error,
    isFetchingNextPage: activeQuery.isFetchingNextPage,
    isFetching: activeQuery.isFetching,
    hasNextPage: activeQuery.hasNextPage,
    jobStats,
    jobStatsStatus,
  };
}
